import { debug } from "./debug";
import { debounce } from "throttle-debounce";

/*------------------------------------*\
  #STACK

  Class for stacking and tracking DOM elements
\*------------------------------------*/
class Stack {
  constructor(){
    // Store elements by position for efficient access and height calculations
    /*
    Array[{
      id: string
      elem: DOM_Element
      position: string
      height: number
      isStack: bool
    }]
    */
    this.elements = {
      top: [],
      bottom: []
    };
  }

  /*------------------------------------*\
    #INITIALISATION
  \*------------------------------------*/
  init = () => {
    if(debug.DEBUG) console.log('helpers/stack.js');

    // SCROLL EVENT with debounce
    this.debouncedScrollHandler = debounce(100, this.scrollHandler);
    document.addEventListener('scroll', this.debouncedScrollHandler);
  }

  getPosition = (position) => {
    const pos = position?.toLowerCase();
    
    if (pos !== 'top' && pos !== 'bottom') return null;

    return pos;
  }

  getElem = (id, position) => {
    // Normalize position to lowercase for consistent access
    const pos = this.getPosition(position);
    if (!pos) return null;
    
    const elemArray = this.elements[pos];
    
    // Find the element with matching ID
    const item = elemArray.find(el => el.id === id);
    
    if (!item) return null; // Element not found

    return item;
  }

  getPreviousStack = (id, position) => {
    const elem = this.getElem(id, position);
    if (!elem) return null;
    let previousStack = 0;

    for (const el of this.elements[position]) {
      if (el.index < elem.index && el.isStack) {
        previousStack += el.height;
      }
    }
    return previousStack;
  }

  create = (id, elem, position, elemTarget ) => {
    if (this.getElem(id, position)) return;

    let target = elemTarget || elem;
    let height = target.offsetHeight;

    this.elements[position].push({
      id,
      elem,
      target,
      height,
      isStack: false,
      index: null,
    });

    // Sort array by DOM position after adding new element
    this.sort(position);

    console.log(this.elements);
  }

  sort = (position) => {
    const pos = position?.toLowerCase();
    
    if (pos !== 'top' && pos !== 'bottom') return;
    
    // Sort elements by their position in the DOM
    this.elements[pos].sort((a, b) => {
      const positionBits = a.elem.compareDocumentPosition(b.elem);
      
      if (positionBits & Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1; // a comes before b in the DOM
      } else if (positionBits & Node.DOCUMENT_POSITION_PRECEDING) {
        return 1; // b comes before a in the DOM
      }
      return 0; // same position (shouldn't happen)
    });
    
    // Update index property after sorting
    this.elements[pos].forEach((item, index) => {
      item.index = index;
    });
  }

  set = (id, position, isStack, height) => {
    
    const item = this.getElem(id, position);
    // console.log(item, id, position);
    if (!item) return;

    // Update the properties
    if (isStack !== undefined) item.isStack = isStack;
    if (height !== undefined) item.height = height;
    
    // Optionally update height from DOM element if not provided
    if (height === undefined && item.elem) {
      item.height = item.target.offsetHeight;
    }
    this.stickyState();
  }

  /**
   * SCROLL HANDLER
   * @param {event} event 
   */
  scrollHandler = (event) => {
    this.stickyState();
  };

  stickyState = () =>  {
    let topPos = 0;
    // Iterate over top position elements
    this.elements.top.forEach((item, index) => {
      const positionY = item.elem.getBoundingClientRect().y;
      if (item.id !== 'Header') item.isStack = positionY <= topPos;
      item.elem.setAttribute('data-js-stack', (positionY <= topPos));
      item.elem.style.setProperty('--stack', `${topPos}px`);
      if (positionY <= topPos && item.isStack) {
        topPos += item.height;
      }
    });

    let bottomGap = 0;
    
    // Iterate over bottom position elements
    this.elements.bottom.forEach((item, index) => {
      item.elem.style.setProperty('--stack', `${bottomGap}px`);
      if (item.isStack) bottomGap += item.height;
    });
  }

}

export const stack = new Stack(); 
document.addEventListener("DOMContentLoaded", function() {
  stack.init();
});
